import React from 'react';

interface StructuredDataViewerProps {
  data: Record<string, any>;
}

export default function StructuredDataViewer({ data }: StructuredDataViewerProps) {
  const downloadJSON = () => {
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `extracted-data-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const renderFieldValue = (value: any): React.ReactNode => {
    if (value === null || value === undefined) {
      return <span className="text-gray-400 italic">No value</span>;
    }
    
    if (Array.isArray(value)) {
      return (
        <div className="space-y-4">
          {value.map((item, index) => (
            <div key={index} className="border border-gray-300 rounded-lg bg-white">
              {/* Item Header */}
              <div className="bg-gray-100 border-b border-gray-300 px-4 py-2 rounded-t-lg">
                <h5 className="text-sm font-semibold text-gray-700">
                  Item {index + 1}
                </h5>
              </div>
              
              {/* Item Content */}
              <div className="p-4">
                {typeof item === 'object' && item !== null ? (
                  <div className="space-y-3">
                    {Object.entries(item).map(([itemKey, itemValue]) => (
                      <div key={itemKey} className="flex gap-3">
                        <div className="w-1/3 px-3 py-2 bg-blue-50 border border-blue-200 rounded text-sm font-medium text-blue-800 min-w-0 flex-shrink-0">
                          <span className="break-words">
                            {itemKey.replace(/([A-Z])/g, ' $1').trim()}
                          </span>
                        </div>
                        <div className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded text-sm min-w-0">
                          <span className="text-gray-800 break-words">
                            {typeof itemValue === 'object' ? JSON.stringify(itemValue) : String(itemValue)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded text-sm">
                    {String(item)}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      );
    }
    
    if (typeof value === 'object') {
      // For nested objects, display them in a structured way
      return (
        <div className="space-y-2">
          {Object.entries(value).map(([nestedKey, nestedValue]) => (
            <div key={nestedKey} className="flex gap-2">
              <div className="w-1/2 px-2 py-1 bg-blue-100 border border-blue-300 rounded text-xs font-medium text-blue-900 min-w-0">
                {nestedKey.replace(/([A-Z])/g, ' $1').trim()}
              </div>
              <div className="w-1/2 px-2 py-1 bg-gray-100 border border-gray-300 rounded text-xs min-w-0">
                {typeof nestedValue === 'object' ? JSON.stringify(nestedValue) : String(nestedValue)}
              </div>
            </div>
          ))}
        </div>
      );
    }
    
    return <span className="text-gray-800 break-words">{String(value)}</span>;
  };

  if (!data || Object.keys(data).length === 0) {
    return (
      <div className="text-center text-gray-500 py-8">
        <i className="ri-file-line text-4xl mb-2"></i>
        <p>No extracted data available</p>
        <p className="text-sm">Upload a document and click Extract to see results</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with download button */}
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-semibold text-gray-800">Extracted Data</h3>
        <button
          onClick={downloadJSON}
          className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
        >
          <i className="ri-download-line"></i>
          <span>Download JSON</span>
        </button>
      </div>

      {/* Structured Data Display */}
      <div className="space-y-6">
        {Object.entries(data).map(([sectionKey, sectionValue]) => (
          <div key={sectionKey} className="bg-white border-2 border-indigo-200 rounded-lg shadow-sm">
            {/* Section Header */}
            <div className="bg-indigo-50 border-b-2 border-indigo-200 px-6 py-4 rounded-t-lg">
              <h4 className="text-lg font-semibold text-indigo-800 capitalize">
                {sectionKey.replace(/([A-Z])/g, ' $1').trim()}
              </h4>
            </div>
            
            {/* Section Content */}
            <div className="p-6">
              {typeof sectionValue === 'object' && !Array.isArray(sectionValue) ? (
                <div className="space-y-4">
                  {Object.entries(sectionValue).map(([fieldKey, fieldValue]) => (
                    <div key={fieldKey} className="flex gap-4 items-start">
                      {/* Field Name Box */}
                      <div className="w-1/3 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg text-sm font-medium text-blue-800 min-w-0 flex-shrink-0">
                        <span className="break-words">
                          {fieldKey.replace(/([A-Z])/g, ' $1').trim()}
                        </span>
                      </div>
                      {/* Field Value Box */}
                      <div className="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm min-w-0">
                        {renderFieldValue(fieldValue)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex gap-4 items-start">
                  <div className="w-1/3 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg text-sm font-medium text-blue-800 min-w-0 flex-shrink-0">
                    Value
                  </div>
                  <div className="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm min-w-0">
                    {renderFieldValue(sectionValue)}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
